/**
 * App-wide reactive state store.
 *
 * We use a single Preact `signal` holding the AppState object. Components
 * that read `state.value` re-render automatically when the signal changes.
 *
 * Mutations go through helpers below — never assign to nested fields
 * directly. The diff-push pipeline (storage.ts) reads the signal value
 * and computes per-table diffs; assignment via the helpers ensures the
 * push fires.
 */
import { signal, computed } from "@preact/signals";
import type { AppState, StatusCode, ViewKey } from "../types";

const initialState: AppState = {
  roster: {},
  calendar: {},
  notes: {},
  meetings: {},
  currentUser: null,
  isAdmin: false,
  isSuperAdmin: false,
  actAs: null,
  profileId: null,
  adminEmail: "",
  profilesByRoster: {},
  closures: {},
};

/** The single reactive AppState signal. Reactivity rule of thumb:
 *  - Reads (`state.value`) inside a component register that component
 *    as a subscriber.
 *  - Mutations: replace the whole signal value, never deeply-mutate.
 *    Use the helpers below for common cases. */
export const state = signal<AppState>(initialState);

/** Currently visible view tab. Separate signal so view changes don't
 *  re-render every component that reads state.value. */
export const currentView = signal<ViewKey>("my");

/** Browsing month / year. Independent of state because users navigate
 *  through months without touching the data. */
export const currentYear = signal<number>(new Date().getFullYear());
export const currentMonth = signal<number>(new Date().getMonth());

/** Currently selected paint code in the My-Calendar palette. */
export const paintCode = signal<StatusCode>("P");

/** Are we in demo mode? Set once when the demo button is clicked.
 *  Disables the auth listener and the diff-push pipeline. */
export const isDemoMode = signal<boolean>(false);

/** Are we in the middle of bootFromSupabase? True from sign-in event
 *  fire until the first hydration completes (typically 1-10s depending
 *  on connection + calendar size). The render layer shows a full-screen
 *  loading overlay while this is true so the user can't click partially-
 *  loaded UI and trigger errors against unhydrated state. */
export const isBooting = signal<boolean>(false);

/** Convenience: which person is the user currently editing?
 *  Admins edit `state.actAs`, regular users always edit themselves. */
export const editingTarget = computed<string | null>(() => {
  const s = state.value;
  return s.isAdmin ? s.actAs : s.currentUser;
});

/** Replace the whole state. Use this from boot, demo, and signOut. */
export function setState(next: AppState): void {
  state.value = next;
}

/** Patch a few top-level keys without re-creating the whole object. */
export function patchState(patch: Partial<AppState>): void {
  state.value = { ...state.value, ...patch };
}

/** Set a single status cell. Returns true if it changed (for diff-push). */
export function setCalendarCell(
  person: string,
  day: string,
  status: StatusCode | null,
): boolean {
  const s = state.value;
  const personMap = { ...(s.calendar[person] ?? {}) };
  const before = personMap[day];
  if (status === null) {
    if (before === undefined) return false;
    delete personMap[day];
  } else {
    if (before === status) return false;
    personMap[day] = status;
  }
  state.value = {
    ...s,
    calendar: { ...s.calendar, [person]: personMap },
  };
  return true;
}

export function setNote(person: string, day: string, text: string | null): boolean {
  const s = state.value;
  const personMap = { ...(s.notes[person] ?? {}) };
  if (text === null || text.length === 0) {
    if (personMap[day] === undefined) return false;
    delete personMap[day];
  } else {
    if (personMap[day] === text) return false;
    personMap[day] = text;
  }
  state.value = { ...s, notes: { ...s.notes, [person]: personMap } };
  return true;
}

/** Flag / unflag an in-presence meeting on a given day. The optional
 *  description is the one-liner shown in the Company Overview's pinned
 *  "In-presence meetings" row. Returns true if the state changed. */
export function setMeeting(
  person: string,
  day: string,
  present: boolean,
  description = "",
): boolean {
  const s = state.value;
  const personMap = { ...(s.meetings[person] ?? {}) };
  if (!present) {
    if (personMap[day] === undefined) return false;
    delete personMap[day];
  } else {
    const before = personMap[day];
    if (before === description) return false;
    personMap[day] = description;
  }
  state.value = { ...s, meetings: { ...s.meetings, [person]: personMap } };
  return true;
}
